"use client";

import { motion } from "framer-motion";
import {
  Wallet,
  Trophy,
  CreditCard,
  History,
  ShieldCheck,
  Coins,
  ArrowRight,
  Sparkles,
} from "lucide-react";

import Section from "./ui/Section";
import ProductShowcase from "./showcase/ProductShowcase";

const rewards = [
  {
    icon: Wallet,
    title: "One place for wallet activity",
    description:
      "Review your available, locked, and bonus balances alongside your wallet activity.",
  },
  {
    icon: Trophy,
    title: "Competition-based rewards",
    description:
      "Eligible competitions may offer rewards based on performance and their published rules.",
  },
  {
    icon: History,
    title: "Transaction history",
    description:
      "Review recorded wallet activity such as credits, entry fees, prizes, and refunds.",
  },
  {
    icon: ShieldCheck,
    title: "Clear reward conditions",
    description:
      "Check the rules and conditions for a competition before participating.",
  },
];

export default function RewardsExperience() {
  return (
    <Section
      id="rewards"
      className="relative overflow-hidden bg-[#020817] py-20 lg:py-28"
    >
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-48 top-1/3 h-[30rem] w-[30rem] rounded-full bg-brand-gold/[0.04] blur-3xl" />
        <div className="absolute -right-48 bottom-[-10rem] h-[34rem] w-[34rem] rounded-full bg-blue-500/[0.04] blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-white/[0.07]" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-white/[0.07]" />
      </div>

      <div className="relative z-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-14">
        {/* Wallet showcase — keep the existing ProductShowcase integration */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative order-2 lg:order-1"
        >
          <div className="pointer-events-none absolute -inset-8 rounded-[4rem] bg-brand-gold/[0.035] blur-3xl" />
          <div className="relative">
            <ProductShowcase mode="wallet" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="relative mx-auto mt-4 max-w-sm rounded-2xl border border-brand-gold/20 bg-[#081126]/95 p-4 shadow-2xl sm:absolute sm:-bottom-5 sm:right-0 sm:mt-0 sm:mx-0 sm:max-w-[250px]"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-gold/20 bg-brand-gold/[0.08] text-brand-gold">
                <Coins className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">
                  TCD Credits
                </p>
                <p className="mt-0.5 text-lg font-black text-white">
                  100 Credits = ₹1
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs leading-5 text-white/45">
              Credit conversion reference. Credits and cash balances are not
              interchangeable in every situation.
            </p>
          </motion.div>
        </motion.div>

        {/* Rewards content */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="order-1 lg:order-2"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/30 bg-brand-gold/[0.05] px-4 py-2">
            <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-brand-gold">
              TCD Wallet & Rewards
            </span>
          </div>

          <h2 className="mt-6 max-w-2xl text-4xl font-black leading-[1.06] tracking-tight text-white md:text-5xl xl:text-[3.4rem]">
            Progress you can track.
            <br className="hidden sm:block" />
            <span className="text-brand-gold">Rewards with clarity.</span>
          </h2>

          <p className="mt-5 max-w-xl text-base leading-7 text-white/60 sm:text-lg sm:leading-8">
            The TCD wallet brings your credit balance and wallet activity
            together. Take part in eligible activities, follow the published
            rules, and keep track of credits or rewards recorded to your account.
          </p>

          <div className="mt-7 rounded-2xl border border-brand-gold/20 bg-brand-gold/[0.045] p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-gold/10 text-brand-gold">
                <Coins className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-white">Understand TCD Credits</h3>
                <p className="mt-1 text-sm leading-6 text-white/60">
                  The current reference conversion is{" "}
                  <span className="font-bold text-brand-gold">
                    100 TCD Credits = ₹1
                  </span>
                  . This is a conversion reference—not a promise that all
                  credits can be withdrawn as cash. Availability and use depend
                  on the applicable wallet and reward rules.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 space-y-3">
            {rewards.map((item, index) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  whileHover={{ y: -2 }}
                  className="group flex items-start gap-4 rounded-2xl border border-white/[0.10] bg-white/[0.025] px-4 py-4 transition-colors duration-300 hover:border-brand-gold/30 hover:bg-white/[0.045] sm:px-5"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-brand-gold/25 bg-brand-gold/[0.05] text-brand-gold transition-colors group-hover:bg-brand-gold/[0.10]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-white">{item.title}</h3>
                    <p className="mt-1 text-sm leading-6 text-white/50">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <a
            href="#exams"
            className="group mt-7 inline-flex min-h-11 items-center gap-2 rounded-xl bg-brand-gold px-5 py-3 text-sm font-bold text-[#07101F] transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-[#020817]"
          >
            Explore the exam experience
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>

          <p className="mt-4 max-w-xl text-xs leading-5 text-white/40">
            Rewards are not guaranteed. Any prize, credit, or withdrawal is
            subject to the applicable competition, wallet, eligibility, and
            platform rules.
          </p>
        </motion.div>
      </div>
    </Section>
  );
}
